import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "flex h-full flex-col gap-4" }
const _hoisted_2 = { class: "flex min-h-0 flex-1 flex-col rounded border relative" }
const _hoisted_3 = {
  key: 0,
  class: "absolute bottom-[15px] inset-0 flex items-center justify-center pointer-events-none animate-[fadeIn_.3s_ease_both]"
}
const _hoisted_4 = { class: "flex items-center gap-2" }
const _hoisted_5 = { class: "flex items-center gap-2" }
const _hoisted_6 = { class: "flex flex-col items-center gap-2" }

import type {
        ColumnDef,
        SortingState,
        ColumnFiltersState,
        VisibilityState,
        ExpandedState,
        Column,
        PaginationState,
        RowSelectionState,
        GroupingState,
        Row,
        Table as TableType,
    } from '@tanstack/vue-table';
    import {
        FlexRender,
        getCoreRowModel,
        useVueTable,
        getPaginationRowModel,
        getSortedRowModel,
        getFilteredRowModel,
        getExpandedRowModel,
        getGroupedRowModel,
        getFacetedRowModel,
        getFacetedUniqueValues,
        getFacetedMinMaxValues,
    } from '@tanstack/vue-table';
    import {
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
    } from '@/components/ui/table';
    import { useVirtualizer } from '@tanstack/vue-virtual';
    import { Checkbox } from '@/components/ui/checkbox';
    import { cn } from '@/lib/utils';
    import { Separator } from '@/components/ui/separator';
    import { useFilter } from 'reka-ui';
    import { valueUpdater } from '@/lib/utils';
    import { ref, computed, onMounted, watch, nextTick } from 'vue';
    import type { ComponentPublicInstance } from 'vue';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import DataTableGroupToggle from '@/components/datatable/DataTableGroupToggle.vue';
    import DataTableOptions from '@/components/datatable/DataTableOptions.vue';
    import DataTableFilters from '@/components/datatable/DataTableFilters.vue';
    import DataTableExportModel from '@/components/datatable/DataTableExportModel.vue';
    import DebouncedInput from '@/components/DebouncedInput.vue';
    import Icon from '@/components/Icon.vue';
    import Loading from '@/components/Loading.vue';
    import {
        booleanFilterFn,
        valueInArrayFilterFn,
        dateRangeFilterFn,
        periodRangeFilterFn,
    } from '@/utils/filterFunctions';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'DataTable',
  props: {
    tableKey: {},
    columns: {},
    data: { default: () => [] },
    loading: { type: Boolean },
    isError: { type: Boolean },
    pinnedColumns: { default: () => ({
                left: [],
                right: [],
            }) },
    defaultSorting: { default: () => [
                {
                    id: 'id',
                    desc: false,
                },
            ] },
    defaultPagination: { default: () => ({
                pageIndex: 0,
                pageSize: 20,
            }) },
    defaultGrouping: { default: () => [] },
    defaultColumnVisibility: { default: () => ({}) },
    defaultColumnFilters: { default: () => [] },
    defaultGlobalFilter: { default: '' },
    autoUrlSearch: { type: Boolean, default: false },
    manualPagination: { type: Boolean, default: false },
    manualSorting: { type: Boolean, default: false },
    manualFiltering: { type: Boolean, default: false },
    totalRecords: {},
    hideControls: { type: Boolean, default: false },
    enableSearch: { type: Boolean, default: true },
    enableFilters: { type: Boolean, default: true },
    enableExport: { type: Boolean, default: true },
    enableToolbar: { type: Boolean, default: true },
    toolbarClass: {},
    meta: { default: () => ({}) },
    virtualize: { type: Boolean },
    virtualRowHeight: {},
    virtualOverscan: {},
    fixedColumnSizes: { default: () => ({}) },
    autoComputeColumnSizes: { default: () => ({}) },
    hydrating: { type: Boolean, default: false }
  },
  emits: ["pagination-change", "sorting-change", "filters-change", "global-filter-change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const STORAGE_KEY = `datatable:filters:${props.tableKey}`;

    // Auto URL search handling
    const { extractAndCleanSearch } = useUrlSearch();

    // Compute the initial global filter value
    const computedDefaultGlobalFilter = computed(() => {
        if (props.autoUrlSearch) {
            const urlSearch = extractAndCleanSearch();
            return urlSearch || props.defaultGlobalFilter;
        }
        return props.defaultGlobalFilter;
    });

    const sorting = ref<SortingState>(props.defaultSorting);

    const columnPinning = ref({
        left: props.pinnedColumns.left,
        right: props.pinnedColumns.right,
    });

    const columnFilters = ref<ColumnFiltersState>(props.defaultColumnFilters);
    const columnVisibility = ref<VisibilityState>(props.defaultColumnVisibility);
    const rowSelection = ref<RowSelectionState>({});
    const expanded = ref<ExpandedState>({});
    const globalFilter = ref(computedDefaultGlobalFilter.value);
    const pagination = ref<PaginationState>(props.defaultPagination);
    const grouping = ref<GroupingState>(props.defaultGrouping);

    const getPinningClasses = (column: Column<TData, any>, isHeader: boolean, depth?: number) => {
        const isPinned = column.getIsPinned();
        const isLeftPinnedColumn = isPinned === 'left';
        const isLastLeftPinnedColumn = isLeftPinnedColumn && column.getIsLastColumn('left');
        const isFirstHeaderColumn =
            isPinned && isHeader && table.getHeaderGroups().length > 1 && depth === 0;

        return {
            'sticky left-0 bg-white inset-shadow-[0_-1px_0_0_var(--table-border)] z-20':
                isLeftPinnedColumn,
            'z-30': isPinned && isHeader,
            'shadow-[inset_-1px_0_0_0_var(--table-border)]':
                isFirstHeaderColumn || isLastLeftPinnedColumn,
        };
    };

    const getBorderClasses = (column: Column<TData, any>) => {
        return {
            'border-secondary border-r': column.columnDef.meta?.border,
        };
    };

    const getFixedWidthPx = (columnId: string) => {
        const w = computedFixedSizes.value?.[columnId];
        if (typeof w === 'number' && w > 0) {
            return `${w}px`;
        }

        return undefined;
    };

    const autoComputedFixed = ref<Record<string, number>>({});

    const getHeaderElForColumn = (columnId: string): HTMLElement | null => {
        const container = (tableContainerRef.value as any)?.getScrollElement?.() ??
            (tableContainerRef.value?.$el as HTMLElement | null) ??
            document;

        return (container as HTMLElement).querySelector?.(
            `[data-header-id="${columnId}"]`,
        ) as HTMLElement | null;
    };

    const getCanvasFontFromElement = (el: HTMLElement | null): string => {
        const defaultFont = '12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
        if (!el) {
            return defaultFont;
        }

        const cs = window.getComputedStyle(el);
        // Prefer computed shorthand font which includes all necessary parts
        const shorthand = (cs as any).font as string | undefined;
        if (shorthand && shorthand.length > 0) {
            return shorthand;
        }

        return `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`.trim() || defaultFont;
    };

    const measureWithCanvas = (text: string, font: string): number => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return text.length * 8; // fallback heuristic
        }

        ctx.font = font;
        const metrics = ctx.measureText(text);

        return Math.ceil(metrics.width);
    };

    // Memoized status id → label maps per column
    const statusLabelMaps = computed<Record<string, Map<string, string>>>(() => {
        const meta = (table?.options?.meta || {}) as any;
        const result: Record<string, Map<string, string>> = {};
        const src = meta.statusOptions || {};
        for (const [colId, options] of Object.entries(src) as Array<[string, any[]]>) {
            const map = new Map<string, string>();
            if (Array.isArray(options)) {
                for (const opt of options) {
                    map.set(String((opt as any)?.value), String((opt as any)?.label ?? ''));
                }
            }
            result[colId] = map;
        }
        
        return result;
    });

    const computeAutoColumnWidths = () => {
        const buffers = props.autoComputeColumnSizes ?? {};
        const rowsModel = table.getRowModel().rows;

        for (const [columnId, bufferPx] of Object.entries(buffers)) {
            if (props.fixedColumnSizes && typeof props.fixedColumnSizes[columnId] === 'number') {
                continue;
            }

            const buffer = typeof bufferPx === 'number' ? bufferPx : 0;

            const col = table.getColumn(columnId);
            const headerEl = getHeaderElForColumn(columnId);
            const font = getCanvasFontFromElement(headerEl);

            let headerLabel: string | undefined;
            const rawHeader = col?.columnDef?.header as any;
            if (typeof rawHeader === 'string') {
                headerLabel = rawHeader;
            } else if (headerEl?.textContent) {
                headerLabel = headerEl.textContent.trim();
            }

            const headerTextWidth = headerLabel ? measureWithCanvas(headerLabel, font) : 0;
            // Include horizontal padding (~16px from px-2) + 16px for sort icons
            const headerBaseline = headerTextWidth + 16 + 16;

            // Measure all row values for the column via table row model
            let maxCellWidth = 0;
            const labelMap = statusLabelMaps.value[columnId];
            const getLabelForValue = (val: any): string => {
                if (labelMap && val != null) {
                    const mapped = labelMap.get(String(val));
                    if (mapped != null) {
                        
                        return mapped;
                    }
                }

                return val instanceof Date ? val.toLocaleString() : String(val ?? '');
            };
            for (const r of rowsModel) {
                const value = r.getValue(columnId) as any;
                const text = getLabelForValue(value);
                const w = measureWithCanvas(text, font);
                if (w > maxCellWidth) {
                    maxCellWidth = w;
                }
            }

            // Total width: max cell width + padding + buffer, but never less than header baseline or 90px
            const computedWidth = Math.max(maxCellWidth + 16 + buffer, headerBaseline, 90);

            autoComputedFixed.value = {
                ...autoComputedFixed.value,
                [columnId]: computedWidth,
            };
        }
    };

    const computedFixedSizes = computed<Record<string, number>>(() => {
        return {
            ...(props.fixedColumnSizes ?? {}),
            ...autoComputedFixed.value,
        };
    });

    const toggleChildrenSelection = (row: Row<TData>, value: any, table: TableType<TData>) => {
        const toggle = (r: Row<TData>) => {
            if (r.subRows?.length) {
                r.subRows.forEach(toggle);
            } else {
                table.getRowModel().rowsById[r.id]?.toggleSelected(!!value);
            }
        };
        if (row.subRows?.length) {
            row.subRows.forEach(toggle);
        }
    };

    const saveFiltersToLocalStorage = () => {
        if (props.tableKey) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(columnFilters.value));
        }
    };

    const { contains } = useFilter({ sensitivity: 'base' });

    const table = useVueTable({
        get data() {
            return props.data;
        },
        get columns() {
            return props.columns;
        },
        get rowCount() {
            if (props.manualPagination && props.totalRecords) {
                return props.totalRecords;
            }
        },

        manualPagination: props.manualPagination,
        manualSorting: props.manualSorting,
        manualFiltering: props.manualFiltering,

        enableColumnFilters: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        filterFromLeafRows: true,
        autoResetExpanded: false,
        autoResetPageIndex: false,
        getSubRows: (row: any) => row.subRows,
        enableRowSelection: (row) => !row.subRows?.length,
        filterFns: {
            boolean: booleanFilterFn,
            valueInArray: valueInArrayFilterFn,
            dateRange: dateRangeFilterFn,
            periodRange: periodRangeFilterFn,
        },
        onSortingChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, sorting);
            emit('sorting-change', sorting.value);
        },
        onColumnFiltersChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, columnFilters);
            emit('filters-change', columnFilters.value);
            saveFiltersToLocalStorage();
            table.setPageIndex(0);
            if (props.autoComputeColumnSizes) {
                nextTick(() => {
                    computeAutoColumnWidths();
                });
            }
        },
        onColumnVisibilityChange: (updaterOrValue) =>
            valueUpdater(updaterOrValue, columnVisibility),
        onRowSelectionChange: (updaterOrValue) => valueUpdater(updaterOrValue, rowSelection),
        onExpandedChange: (updaterOrValue) => valueUpdater(updaterOrValue, expanded),
        onGlobalFilterChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, globalFilter);
            emit('global-filter-change', globalFilter.value);
            table.setPageIndex(0);
            if (props.autoComputeColumnSizes) {
                nextTick(() => {
                    computeAutoColumnWidths();
                });
            }
        },
        onPaginationChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, pagination);
            emit('pagination-change', pagination.value);
        },
        onColumnPinningChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnPinning),
        onGroupingChange: (updaterOrValue) => valueUpdater(updaterOrValue, grouping),
        globalFilterFn: props.manualFiltering
            ? undefined
            : (row, columnId, filterValue) => {
                  let currentRow = row;

                  while (currentRow.parentId) {
                      const parentRow = table.getRow(currentRow.parentId);
                      if (contains(String(parentRow.getValue(columnId)), filterValue)) {
                          return true;
                      }
                      currentRow = parentRow;
                  }

                  return contains(String(row.getValue(columnId)), filterValue);
              },
        meta: props.meta,
        state: {
            get sorting() {
                return sorting.value;
            },
            get columnFilters() {
                return columnFilters.value;
            },
            get columnVisibility() {
                return columnVisibility.value;
            },
            get rowSelection() {
                return rowSelection.value;
            },
            get expanded() {
                return expanded.value;
            },
            get globalFilter() {
                return globalFilter.value;
            },
            get pagination() {
                return pagination.value;
            },
            get columnPinning() {
                return columnPinning.value;
            },
            get grouping() {
                return grouping.value;
            },
        },
    });

    const fallbackHydratingOverlayLeftPx = computed(() => {
        return (columnPinning.value.left ?? []).reduce((width, columnId) => {
            const fixedWidth = computedFixedSizes.value[columnId];

            if (typeof fixedWidth === 'number' && fixedWidth > 0) {
                return width + fixedWidth;
            }

            const column = table.getColumn(columnId);

            return width + (column?.getSize?.() ?? column?.columnDef.size ?? 0);
        }, 0);
    });

    const measuredHydratingOverlayLeftPx = ref<number | null>(null);

    const updateHydratingOverlayLeftPx = async () => {
        await nextTick();

        const scrollElement = (tableContainerRef.value as any)?.getScrollElement?.() as HTMLElement | null;
        const tableFrame = scrollElement?.parentElement;

        if (!scrollElement || !tableFrame) {
            measuredHydratingOverlayLeftPx.value = null;

            return;
        }

        const frameRect = tableFrame.getBoundingClientRect();
        const pinnedRightEdge = (columnPinning.value.left ?? []).reduce((rightEdge, columnId) => {
            const headerEl = getHeaderElForColumn(columnId);

            if (!headerEl) {
                return rightEdge;
            }

            const headerRect = headerEl.getBoundingClientRect();

            return Math.max(rightEdge, Math.ceil(headerRect.right - frameRect.left));
        }, 0);

        measuredHydratingOverlayLeftPx.value = pinnedRightEdge > 0 ? pinnedRightEdge : null;
    };

    const hydratingOverlayLeftPx = computed(() => (
        measuredHydratingOverlayLeftPx.value ?? fallbackHydratingOverlayLeftPx.value
    ));

    const hydratingOverlayStyle = computed(() => ({
        left: `${hydratingOverlayLeftPx.value}px`,
        right: '15px',
    }));

    const tableContainerRef = ref<ComponentPublicInstance | null>(null);
    const rowVirtualizerOptions = computed(() => {
        return {
            count: table.getRowModel().rows.length,
            estimateSize: () => props.virtualRowHeight, //estimate row height for accurate scrollbar dragging
            getItemKey: (index: number) => table.getRowModel().rows[index]?.id,
            getScrollElement: () => {
                const exposedGetter = (tableContainerRef.value as any)?.getScrollElement;
                if (typeof exposedGetter === 'function') {
                    return exposedGetter();
                }

                return (tableContainerRef.value?.$el as HTMLDivElement | null);
            },
            overscan: props.virtualOverscan,
        }
    })

const rowVirtualizer = useVirtualizer(rowVirtualizerOptions)

const rows = computed(() => table.getRowModel().rows)
const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems())
const totalSize = computed(() => rowVirtualizer.value.getTotalSize())

const paddingTop = computed(() => {
    const v = virtualRows.value
    if (!v.length) {
        return 0
    }

    return v[0].start
})

const paddingBottom = computed(() => {
    const v = virtualRows.value
    if (!v.length) {
        return 0
    }

    const last = v[v.length - 1]

    return totalSize.value - last.end
})

    __expose({
        table,
    });

    onMounted(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            columnFilters.value = JSON.parse(saved);
        }
        if (props.autoComputeColumnSizes) {
            computeAutoColumnWidths();
        }
        void updateHydratingOverlayLeftPx();
    });

    // Recompute on data changes (e.g., after async loads)
    watch(
        () => props.data,
        async () => {
            // Wait for DOM (header) to exist so we can read its computed font
            await nextTick();
            if (props.autoComputeColumnSizes) {
                computeAutoColumnWidths();
            }
            void updateHydratingOverlayLeftPx();
        },
        { deep: true }
    );

    watch(
        [computedFixedSizes, () => columnPinning.value.left, () => props.hydrating],
        () => {
            void updateHydratingOverlayLeftPx();
        },
        { deep: true },
    );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.enableToolbar)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(_unref(cn)('flex flex-wrap gap-4 sm:items-center', _ctx.toolbarClass))
        }, [
          _renderSlot(_ctx.$slots, "title", { table: _unref(table) }),
          (_ctx.enableSearch || _ctx.enableFilters || _ctx.$slots.filters)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["flex items-center gap-2", _ctx.enableSearch && 'grow sm:max-w-sm'])
              }, [
                _createVNode(_unref(Separator), {
                  orientation: "vertical",
                  class: "mr-2 hidden h-2.5! md:flex"
                }),
                (_ctx.enableSearch)
                  ? (_openBlock(), _createBlock(DebouncedInput, {
                      key: 0,
                      placeholder: _ctx.$t('datatables.search_placeholder'),
                      modelValue: globalFilter.value ?? '',
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = (value) => _unref(table).setGlobalFilter(String(value))),
                      debounce: 200
                    }, null, 8, ["placeholder", "modelValue"]))
                  : _createCommentVNode("", true),
                (_ctx.enableFilters)
                  ? (_openBlock(), _createBlock(DataTableFilters, {
                      key: 1,
                      table: _unref(table)
                    }, null, 8, ["table"]))
                  : _createCommentVNode("", true),
                _renderSlot(_ctx.$slots, "filters", { table: _unref(table) })
              ], 2))
            : _createCommentVNode("", true),
          (_ctx.$slots.actions)
            ? (_openBlock(), _createBlock(_unref(Separator), {
                key: 1,
                orientation: "vertical",
                class: "hidden h-2.5! md:flex"
              }))
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "actions", { table: _unref(table) }),
          _createVNode(DataTableGroupToggle, { table: _unref(table) }, null, 8, ["table"]),
          (_ctx.enableExport)
            ? (_openBlock(), _createBlock(DataTableExportModel, {
                key: 2,
                table: _unref(table)
              }, null, 8, ["table"]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    _renderSlot(_ctx.$slots, "table-top", { table: _unref(table) }),
    _createElementVNode("div", _hoisted_2, [
      (_ctx.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(Loading)
          ]))
        : (_openBlock(), _createBlock(_unref(Table), {
            key: 1,
            ref_key: "tableContainerRef",
            ref: tableContainerRef
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(TableHeader), { class: "relative z-20" }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getHeaderGroups(), (headerGroup) => {
                    return (_openBlock(), _createBlock(_unref(TableRow), {
                      key: headerGroup.id
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerGroup.headers, (header) => {
                          return (_openBlock(), _createBlock(_unref(TableHead), {
                            key: header.id,
                            class: _normalizeClass([
                                getPinningClasses(header.column, true, headerGroup.depth),
                                getBorderClasses(header.column),
                            ]),
                            style: _normalizeStyle({
                                width: getFixedWidthPx(header.column.id) ?? undefined,
                                minWidth: getFixedWidthPx(header.column.id) ?? undefined,
                                maxWidth: getFixedWidthPx(header.column.id) ?? undefined,
                                left:
                                    header.column.getIsPinned() === 'left' &&
                                    headerGroup ===
                                        _unref(table).getHeaderGroups()[_unref(table).getHeaderGroups().length - 1]
                                        ? `${header.column.getStart('left')}px`
                                        : undefined,
                                top: `${headerGroup.depth * 40}px`,
                            }),
                            colSpan: header.colSpan,
                            "data-header-id": header.id
                          }, {
                            default: _withCtx(() => [
                              (!header.isPlaceholder)
                                ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                    key: 0,
                                    render: header.column.columnDef.header,
                                    props: header.getContext()
                                  }, null, 8, ["render", "props"]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1032, ["class", "style", "colSpan", "data-header-id"]))
                        }), 128))
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]),
                _: 1
              }),
              _createVNode(_unref(TableBody), null, {
                default: _withCtx(() => [
                  (!_ctx.virtualize && _unref(table).getRowModel().rows?.length)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(table).getRowModel().rows, (row) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: row.id
                        }, [
                          (row.getCanExpand())
                            ? (_openBlock(), _createBlock(_unref(TableRow), {
                                key: 0,
                                class: _normalizeClass('bg-table-expanded-row font-medium'),
                                "data-state": row.getIsSelected() ? 'selected' : undefined
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(TableCell), { class: "sticky left-0" }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Checkbox), {
                                        "model-value": 
                                            row.getIsSomeSelected()
                                                ? 'indeterminate'
                                                : row.getIsAllSubRowsSelected()
                                        ,
                                        "onUpdate:modelValue": 
                                            (value) => toggleChildrenSelection(row, value, _unref(table))
                                        ,
                                        "aria-label": "Select group"
                                      }, null, 8, ["model-value", "onUpdate:modelValue"])
                                    ]),
                                    _: 2
                                  }, 1024),
                                  _createVNode(_unref(TableCell), {
                                    class: "sticky cursor-pointer",
                                    style: { left: '24px' },
                                    onClick: ($event: any) => (row.getToggleExpandedHandler()())
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("span", _hoisted_4, [
                                        _createTextVNode(_toDisplayString(row.getValue('name')) + " (" + _toDisplayString(row.subRows?.length) + ") ", 1),
                                        _createVNode(Icon, {
                                          class: _normalizeClass(["size-4 transition-transform duration-100", row.getIsExpanded() ? 'rotate-90' : '']),
                                          name: "chevronRight"
                                        }, null, 8, ["class"])
                                      ])
                                    ]),
                                    _: 2
                                  }, 1032, ["onClick"]),
                                  _createVNode(_unref(TableCell), {
                                    class: "cursor-pointer",
                                    colspan: 
                                        row.getVisibleCells().length -
                                        row.getLeftVisibleCells().length
                                    ,
                                    onClick: ($event: any) => (row.getToggleExpandedHandler()())
                                  }, null, 8, ["colspan", "onClick"])
                                ]),
                                _: 2
                              }, 1032, ["data-state"]))
                            : (_openBlock(), _createBlock(_unref(TableRow), {
                                key: 1,
                                "data-state": row.getIsSelected() ? 'selected' : undefined,
                                class: _normalizeClass({ '[&_td]:bg-table-expanded-row': row.depth > 0 })
                              }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                                    return (_openBlock(), _createBlock(_unref(TableCell), {
                                      key: cell.id,
                                      class: _normalizeClass([
                                        getPinningClasses(cell.column, false),
                                        ['actions', 'payslip_actions'].includes(cell.column.id)
                                            ? 'w-0 whitespace-nowrap'
                                            : '',
                                        cell.getIsGrouped() ? 'font-medium' : '',
                                        cell.getIsAggregated() ? 'text-muted-foreground' : '',
                                        getBorderClasses(cell.column),
                                    ]),
                                      style: _normalizeStyle({
                                        width: getFixedWidthPx(cell.column.id) ?? undefined,
                                        minWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        maxWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        left: cell.column.getIsPinned() === 'left'
                                            ? `${cell.column.getStart('left')}px`
                                            : undefined,
                                    })
                                    }, {
                                      default: _withCtx(() => [
                                        (cell.getIsAggregated())
                                          ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                              key: 0,
                                              render: 
                                                cell.column.columnDef.aggregatedCell ||
                                                cell.column.columnDef.cell
                                            ,
                                              props: cell.getContext()
                                            }, null, 8, ["render", "props"]))
                                          : (_openBlock(), _createBlock(_unref(FlexRender), {
                                              key: 1,
                                              render: cell.column.columnDef.cell,
                                              props: cell.getContext()
                                            }, null, 8, ["render", "props"]))
                                      ]),
                                      _: 2
                                    }, 1032, ["class", "style"]))
                                  }), 128))
                                ]),
                                _: 2
                              }, 1032, ["data-state", "class"]))
                        ], 64))
                      }), 128))
                    : (_ctx.virtualize && _unref(table).getRowModel().rows?.length)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          (paddingTop.value)
                            ? (_openBlock(), _createBlock(_unref(TableRow), {
                                key: 0,
                                "aria-hidden": "true",
                                class: "pointer-events-none"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(TableCell), {
                                    colspan: _ctx.columns.length,
                                    class: "p-0"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", {
                                        style: _normalizeStyle({ height: `${paddingTop.value}px` })
                                      }, null, 4)
                                    ]),
                                    _: 1
                                  }, 8, ["colspan"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(virtualRows.value, (vRow) => {
                            return (_openBlock(), _createElementBlock(_Fragment, {
                              key: rows.value[vRow.index].id
                            }, [
                              (rows.value[vRow.index].getCanExpand())
                                ? (_openBlock(), _createBlock(_unref(TableRow), {
                                    key: 0,
                                    class: _normalizeClass('bg-table-expanded-row font-medium animate-[fadeIn_.3s_ease_both]'),
                                    style: _normalizeStyle({ height: `${props.virtualRowHeight}px` }),
                                    "data-state": rows.value[vRow.index].getIsSelected() ? 'selected' : undefined
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(TableCell), { class: "sticky left-0" }, {
                                        default: _withCtx(() => [
                                          _createVNode(_unref(Checkbox), {
                                            "model-value": 
                                            rows.value[vRow.index].getIsSomeSelected()
                                                ? 'indeterminate'
                                                : rows.value[vRow.index].getIsAllSubRowsSelected()
                                        ,
                                            "onUpdate:modelValue": 
                                            (value) => toggleChildrenSelection(rows.value[vRow.index], value, _unref(table))
                                        ,
                                            "aria-label": "Select group"
                                          }, null, 8, ["model-value", "onUpdate:modelValue"])
                                        ]),
                                        _: 2
                                      }, 1024),
                                      _createVNode(_unref(TableCell), {
                                        class: "sticky cursor-pointer",
                                        style: { left: '24px' },
                                        onClick: ($event: any) => (rows.value[vRow.index].getToggleExpandedHandler()())
                                      }, {
                                        default: _withCtx(() => [
                                          _createElementVNode("span", _hoisted_5, [
                                            _createTextVNode(_toDisplayString(rows.value[vRow.index].getValue('name')) + " (" + _toDisplayString(rows.value[vRow.index].subRows?.length) + ") ", 1),
                                            _createVNode(Icon, {
                                              class: _normalizeClass(["size-4 transition-transform duration-100", rows.value[vRow.index].getIsExpanded() ? 'rotate-90' : '']),
                                              name: "chevronRight"
                                            }, null, 8, ["class"])
                                          ])
                                        ]),
                                        _: 2
                                      }, 1032, ["onClick"]),
                                      _createVNode(_unref(TableCell), {
                                        class: "cursor-pointer",
                                        colspan: 
                                        rows.value[vRow.index].getVisibleCells().length -
                                        rows.value[vRow.index].getLeftVisibleCells().length
                                    ,
                                        onClick: ($event: any) => (rows.value[vRow.index].getToggleExpandedHandler()())
                                      }, null, 8, ["colspan", "onClick"])
                                    ]),
                                    _: 2
                                  }, 1032, ["style", "data-state"]))
                                : (_openBlock(), _createBlock(_unref(TableRow), {
                                    key: 1,
                                    "data-state": rows.value[vRow.index].getIsSelected() ? 'selected' : undefined,
                                    style: _normalizeStyle({ height: `${props.virtualRowHeight}px` }),
                                    class: _normalizeClass(["animate-[fadeIn_.3s_ease_both]", { '[&_td]:bg-table-expanded-row': rows.value[vRow.index].depth > 0 }])
                                  }, {
                                    default: _withCtx(() => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value[vRow.index].getVisibleCells(), (cell) => {
                                        return (_openBlock(), _createBlock(_unref(TableCell), {
                                          key: cell.id,
                                          class: _normalizeClass([
                                        getPinningClasses(cell.column, false),
                                        ['actions', 'payslip_actions'].includes(cell.column.id)
                                            ? 'w-0 whitespace-nowrap'
                                            : '',
                                        cell.getIsGrouped() ? 'font-medium' : '',
                                        cell.getIsAggregated() ? 'text-muted-foreground' : '',
                                        getBorderClasses(cell.column),
                                    ]),
                                          style: _normalizeStyle({
                                        width: getFixedWidthPx(cell.column.id) ?? undefined,
                                        minWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        maxWidth: getFixedWidthPx(cell.column.id) ?? undefined,
                                        left: cell.column.getIsPinned() === 'left'
                                            ? `${cell.column.getStart('left')}px`
                                            : undefined,
                                    })
                                        }, {
                                          default: _withCtx(() => [
                                            (cell.getIsAggregated())
                                              ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                                  key: 0,
                                                  render: 
                                                cell.column.columnDef.aggregatedCell ||
                                                cell.column.columnDef.cell
                                            ,
                                                  props: cell.getContext()
                                                }, null, 8, ["render", "props"]))
                                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                  (!props.hydrating || ['name','select'].includes(cell.column.id))
                                                    ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                                        key: 0,
                                                        render: cell.column.columnDef.cell,
                                                        props: cell.getContext()
                                                      }, null, 8, ["render", "props"]))
                                                    : _createCommentVNode("", true)
                                                ], 64))
                                          ]),
                                          _: 2
                                        }, 1032, ["class", "style"]))
                                      }), 128))
                                    ]),
                                    _: 2
                                  }, 1032, ["data-state", "style", "class"]))
                            ], 64))
                          }), 128)),
                          (paddingBottom.value)
                            ? (_openBlock(), _createBlock(_unref(TableRow), {
                                key: 1,
                                "aria-hidden": "true",
                                class: "pointer-events-none"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(TableCell), {
                                    colspan: _ctx.columns.length,
                                    class: "p-0"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", {
                                        style: _normalizeStyle({ height: `${paddingBottom.value}px` })
                                      }, null, 4)
                                    ]),
                                    _: 1
                                  }, 8, ["colspan"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ], 64))
                      : (_openBlock(), _createBlock(_unref(TableRow), { key: 2 }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(TableCell), {
                              colspan: _ctx.columns.length,
                              class: "h-24 text-center"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_6, [
                                  (_ctx.isError)
                                    ? (_openBlock(), _createBlock(Icon, {
                                        key: 0,
                                        name: "alertCircle",
                                        class: "text-destructive size-6"
                                      }))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("span", null, _toDisplayString(_ctx.$t('datatables.infoEmpty')), 1)
                                ])
                              ]),
                              _: 1
                            }, 8, ["colspan"])
                          ]),
                          _: 1
                        }))
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 512)),
      (!_ctx.loading && props.hydrating)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: "absolute bottom-[15px] inset-0 z-10 bg-white/60 backdrop-blur-sm flex items-center justify-center pointer-events-none animate-[fadeIn_.3s_ease_both]",
            style: _normalizeStyle(hydratingOverlayStyle.value)
          }, [
            _createVNode(Loading)
          ], 4))
        : _createCommentVNode("", true)
    ]),
    _createVNode(DataTableOptions, {
      table: _unref(table),
      "manual-pagination": props.manualPagination,
      "total-records": _ctx.totalRecords,
      "hide-controls": props.hideControls
    }, null, 8, ["table", "manual-pagination", "total-records", "hide-controls"])
  ]))
}
}

})